/**
 * Sending a typed chat message: quota/lock/trial checks, optimistic bubbles,
 * creating the session in "New chat" mode (CHAT-3), dispatching with an
 * Idempotency-Key, waiting for the reply task, and restoring the draft on
 * failure (CHAT-2). Returns a plain function rebuilt each render, as before,
 * so it always sees the latest input and session.
 */
import { showDialog } from '@/utils/dialogs';
import { trackAuthEvent } from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';
import { chatAPI } from '../../services/api';
import { chatSendIntentInput, type IdempotencyIntent } from '../../services/idempotencyIntent';
import type { ChatMessage, ChatSession, UnifiedAIResponse } from '../../types';
import { trialAnalysisUsedMessage } from '../../utils/trialAnalysisUsed';
import { STREAMING_BUBBLE_ID, WAITING_BUBBLE_ID, type PendingTask } from './chatConstants';
import {
  chatSendErrorKey,
  chatSendErrorMessage,
  errorStatusOf,
  restoreDraftAfterFailure,
} from './chatErrors';
import type {
  LoadMessages,
  LoadSessions,
  Loader,
  SetMessages,
  SetState,
  TaskCompletion,
  TFn,
  TrialState,
} from './chatFlowTypes';
import { resolveSendTarget, type ConversationGuard } from './conversationGuard';
import type { useChatQuota } from './useChatQuota';
import type { ChatOperationRefs } from './useChatOperationRefs';

export function useChatSend({
  ops,
  guard,
  inputText,
  setInputText,
  sessions,
  currentSessionId,
  language,
  t,
  waitingTipKey,
  chatLocked,
  paidChatLimitExceeded,
  quotaStatus,
  trialStatus,
  isTrialSession,
  refreshTrialStatus,
  setShowTrialLimitModal,
  setMessages,
  setSendingMessage,
  setActiveTaskId,
  chatSendIntent,
  adoptSession,
  addOptimisticMessage,
  addPendingTask,
  removePendingTask,
  loadMessages,
  loadSessions,
  loadQuotaStatus,
  waitForTaskResult,
  showTip,
}: {
  ops: ChatOperationRefs;
  guard: ConversationGuard;
  inputText: string;
  setInputText: SetState<string>;
  sessions: ChatSession[];
  currentSessionId: number | null;
  language: string;
  t: TFn;
  waitingTipKey: string;
  chatLocked: boolean;
  paidChatLimitExceeded: boolean;
  quotaStatus: ReturnType<typeof useChatQuota>['quotaStatus'];
  trialStatus: TrialState['trialStatus'];
  isTrialSession: TrialState['isTrialSession'];
  refreshTrialStatus: TrialState['refreshTrialStatus'];
  setShowTrialLimitModal: SetState<boolean>;
  setMessages: SetMessages;
  setSendingMessage: SetState<boolean>;
  setActiveTaskId: SetState<string | null>;
  /** A typed message the server may turn into a billed re-assessment. */
  chatSendIntent: IdempotencyIntent;
  adoptSession: (sessionId: number) => void;
  addOptimisticMessage: (sessionId: number, message: ChatMessage) => void;
  addPendingTask: (task: PendingTask) => void;
  removePendingTask: (taskId: string) => void;
  loadMessages: LoadMessages;
  loadSessions: LoadSessions;
  loadQuotaStatus: Loader;
  waitForTaskResult: TaskCompletion['waitForTaskResult'];
  showTip: (tipId: string) => void;
}) {
  const {
    activeOperationSessionRef,
    handleToolCallsRef,
    isSendingInProgressRef,
    messageCountRef,
    optimisticMessagesRef,
    pendingScrollToBottomRef,
    sessionHasAnalysisRef,
    shouldAutoScrollRef,
    suppressBubbleRef,
    taskWaiterRef,
  } = ops;

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    // CRITICAL: Synchronous guard to prevent double-execution
    if (isSendingInProgressRef.current) {
      console.log('🚫 Message send already in progress (ref guard), skipping duplicate call');
      return;
    }
    isSendingInProgressRef.current = true;
    console.log('🔒 Send message ref guard set to true');

    if (paidChatLimitExceeded && quotaStatus) {
      isSendingInProgressRef.current = false;
      trackAuthEvent('chat_quota_exceeded', {
        limit_type: 'daily_chats',
        used: quotaStatus.usage.daily_chats_used,
        limit: quotaStatus.usage.daily_chats_limit,
      });
      const message = `${t('chat.quotaExceeded')}\n\n${t('chat.dailyLimitMessage').replace('{{limit}}', String(quotaStatus.usage.daily_chats_limit))}`;
      showDialog(message);
      return;
    }

    if (chatLocked) {
      // The composer is disabled; this guards programmatic sends too.
      isSendingInProgressRef.current = false;
      return;
    }

    // Check trial chat limit
    if (trialStatus?.active && currentSessionId !== null && isTrialSession(currentSessionId) && trialStatus.messages_remaining <= 0) {
      isSendingInProgressRef.current = false;
      setShowTrialLimitModal(true);
      return;
    }

    const userMessage = inputText.trim();
    const messageLength = userMessage.length;
    const wordCount = userMessage.split(/\s+/).filter(Boolean).length;
    setInputText('');
    setSendingMessage(true);
    const sendStart = Date.now();
    let pendingTaskId: string | null = null;
    let operationSessionId: number | null = currentSessionId ?? null;
    let targetSessionId: number | undefined;
    let needsNewSession = false;

    // Add optimistic UI messages
    const tempUserId = Date.now();
    const tempUserMessage: ChatMessage = {
      id: tempUserId,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    const thinkingMessage: ChatMessage = {
      id: WAITING_BUBBLE_ID,
      role: 'assistant',
      content: t(waitingTipKey),
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [
      ...prev.filter((message) => message.id !== WAITING_BUBBLE_ID),
      tempUserMessage,
      thinkingMessage,
    ]);
    shouldAutoScrollRef.current = true;
    pendingScrollToBottomRef.current = true;

    try {
      // "New chat" (or no sessions at all) creates the session first so the
      // send, the optimistic bubble and later attachments share one session.
      const sendTarget = resolveSendTarget(guard, sessions[0]?.id ?? null);
      needsNewSession = sendTarget.kind === 'create';
      if (sendTarget.kind === 'create') {
        const pre = await chatAPI.createSession(t('chat.newChat'));
        targetSessionId = pre.id;
      } else {
        targetSessionId = sendTarget.sessionId;
      }
      operationSessionId = targetSessionId;

      // Register the optimistic message and the operation before the session
      // goes on screen, so the load triggered by the switch keeps them.
      addOptimisticMessage(targetSessionId, tempUserMessage);
      activeOperationSessionRef.current = targetSessionId;
      if (!guard.isViewing(targetSessionId) && (guard.startingNew || guard.viewSessionId == null)) {
        adoptSession(targetSessionId);
      }
      if (needsNewSession) {
        await loadSessions();
      }

      trackAuthEvent('chat_message_sent', {
        session_id: targetSessionId ?? null,
        has_attachment: false,
        message_length: messageLength,
        word_count: wordCount,
        is_new_session: needsNewSession,
      });

      const asyncResponse = await chatAPI.sendMessage({
        message: userMessage,
        session_id: targetSessionId,
        language,
        idempotency_key: chatSendIntent.key(chatSendIntentInput(targetSessionId, userMessage)),
      });
      // The server answered: any job is now known by its task id.
      chatSendIntent.reset();

      if (asyncResponse.status === 'CLARIFICATION_NEEDED') {
        const prompt = asyncResponse.clarification_prompt || t('chat.uploadTranscriptPromptMessage');
        if (guard.isViewing(targetSessionId)) {
          setMessages(prev => [
            ...prev.filter((m) => m.id !== WAITING_BUBBLE_ID),
            {
              id: Date.now() + 1,
              role: 'assistant',
              content: prompt,
              created_at: new Date().toISOString(),
            } as ChatMessage,
          ]);
          shouldAutoScrollRef.current = true;
          pendingScrollToBottomRef.current = true;
        }
        return;
      }

      if (asyncResponse.status === 'TOOL_REQUIRED' && asyncResponse.pending_tool_calls?.length) {
        handleToolCallsRef.current(asyncResponse.pending_tool_calls, asyncResponse.detected_intent);
        return;
      }

      // Handle sync-completed responses (e.g. marker followup from stored data).
      // The composer stays busy until `finally` releases the send guard too:
      // re-enabling it earlier let a quick next send hit the guard and be
      // dropped silently. The suppressed bubble covers the stale
      // sendingMessage that loadMessages would otherwise see.
      if (asyncResponse.status === 'COMPLETE_SYNC') {
        const sid = asyncResponse.session_id ?? targetSessionId;
        if (sid) {
          suppressBubbleRef.current = true;
          optimisticMessagesRef.current.delete(sid);
          await loadMessages(sid);
          await loadQuotaStatus();
        }
        return;
      }

      if (!asyncResponse.task_id) {
        throw new Error('No task id returned');
      }
      pendingTaskId = asyncResponse.task_id;

      const pendingTask: PendingTask = {
        taskId: asyncResponse.task_id,
        sessionId: asyncResponse.session_id ?? targetSessionId ?? 0,
        kind: 'chat',
        createdAt: Date.now(),
        userMessage,
      };
      if (pendingTask.sessionId) {
        addPendingTask(pendingTask);
      }
      operationSessionId = asyncResponse.session_id ?? targetSessionId ?? null;

      // The server may have re-pointed the session; follow it only if the
      // user is still on this conversation.
      if (
        asyncResponse.session_id &&
        asyncResponse.session_id !== targetSessionId &&
        guard.isViewing(targetSessionId)
      ) {
        activeOperationSessionRef.current = asyncResponse.session_id;
        adoptSession(asyncResponse.session_id);
      }

      // Wait for completion via WebSocket (with polling fallback)
      taskWaiterRef.current.add(asyncResponse.task_id);
      setActiveTaskId(asyncResponse.task_id);
      const completionResult = await waitForTaskResult<UnifiedAIResponse>(asyncResponse.task_id);
      setActiveTaskId(null);
      if (completionResult?.status === 'WAITING_FOR_CLIENT_TOOL' && completionResult.pending_tool_calls?.length) {
        if (guard.isViewing(operationSessionId)) {
          setMessages(prev => prev.filter(m => m.id !== WAITING_BUBBLE_ID));
        }
        handleToolCallsRef.current(completionResult.pending_tool_calls, completionResult.detected_intent);
        return;
      }

      // Reload messages from server for consistency (a no-op if the user has
      // switched to another conversation meanwhile).
      const sid = operationSessionId;
      if (sid) {
        // Suppress the thinking bubble until `finally`: sendingMessage stays
        // true (and the composer busy) until the send guard is released, so
        // a next send is never dropped by the guard while it looks enabled.
        suppressBubbleRef.current = true;
        // Clear optimistic messages — server is now authoritative
        optimisticMessagesRef.current.delete(sid);
        await loadMessages(sid);

        // Refresh sessions list if this was new
        if (needsNewSession) {
          await loadSessions();
        }

        await loadQuotaStatus();

        // Refresh trial status after message sent in a trial session
        if (trialStatus?.active && currentSessionId !== null && isTrialSession(currentSessionId)) {
          refreshTrialStatus();
        }
      }

      trackAuthEvent('chat_message_succeeded', {
        session_id: sid ?? null,
        duration_ms: Date.now() - sendStart,
      });

      // Track message count for contextual tips
      messageCountRef.current += 1;
      if (sessionHasAnalysisRef.current && messageCountRef.current >= 5) {
        showTip('tip_new_transcript_reminder');
      }
    } catch (error: any) {
      if (guard.isViewing(operationSessionId) || operationSessionId == null) {
        setMessages(prev => prev.filter(m => m.id !== tempUserId && m.id !== WAITING_BUBBLE_ID && m.id !== STREAMING_BUBBLE_ID));
      }
      if (operationSessionId) {
        optimisticMessagesRef.current.delete(operationSessionId);
      }
      // Give the typed text back so a network blip never loses it (CHAT-2).
      setInputText((current) => restoreDraftAfterFailure(current, userMessage));

      const errorStatus = errorStatusOf(error);
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('chat_message_failed', {
        session_id: targetSessionId ?? null,
        error_type: errorType,
        error_key: chatSendErrorKey(error),
        // Stable code: document_has_no_transcript, provider_unavailable, http_429, ...
        error_code: analyticsErrorCode(error),
        duration_ms: Date.now() - sendStart,
      });

      // Translated text only; never the backend's English detail or
      // "HTTP 502" / "Failed to fetch" (CHAT-19). A spent card-backed trial
      // analysis explains when the plan starts instead.
      showDialog(trialAnalysisUsedMessage(t, language, error) || chatSendErrorMessage(t, error));
    } finally {
      if (pendingTaskId) {
        removePendingTask(pendingTaskId);
        taskWaiterRef.current.delete(pendingTaskId);
      }
      setSendingMessage(false);
      activeOperationSessionRef.current = null;
      suppressBubbleRef.current = false;
      isSendingInProgressRef.current = false;
      console.log('🔓 Send message ref guard cleared, active operation session cleared');
    }
  };

  return sendMessage;
}
